import { Icon, useBreakpoint } from '@main/core-ui';
import { getUnderlyingPath } from '@main/internationalization';
import { SkipLinkIds } from '@main/pc-types/src/schema/enums/skipLinks';
import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import React, { useEffect, useRef, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { navMenuMessages } from './messages';
import { NavMenuLink } from './NavMenuLink';
import {
  FullPageWrapper,
  LinksWrapper,
  NavBarWrapper,
  NavMenuWrapper,
  NavToggleIcon,
  NavToggleWrapper,
} from './wrappers';

/**
 * NavMenu Props
 */
export interface NavMenuProps {
  /** The menu items */
  links: {
    /** Link itself */
    href: string;
    /** Message definition */
    message: MessageDescriptor;
  }[];
  /** The menu wrapper */
  Wrapper?: typeof NavMenuWrapper;
}

/**
 * NavMenu state
 */
export interface NavMenuState {
  /** Current path */
  pathname: string;
  /** Indicates if burger is open */
  isOpen: boolean;
}

/**
 * The NavMenu
 */
export function NavMenu({
  links,
  Wrapper = NavMenuWrapper,
}: NavMenuProps): JSX.Element {
  const location = useLocation();
  const { formatMessage } = useIntl();

  const fullPageNavMenuRef = useRef<HTMLDivElement>(null);
  const navToggleWrapperRef = useRef<HTMLDivElement>(null);
  const pathnameRef = useRef<string>('');

  const [isOpen, setIsOpen] = useState(false);

  /** Opens nav menu */
  const openNav = (): void => {
    setIsOpen(true);
    if (fullPageNavMenuRef.current) {
      disableBodyScroll(fullPageNavMenuRef.current);
    }
  };

  /** Closes nav menu */
  const closeNav = (): void => {
    setIsOpen(false);
    if (fullPageNavMenuRef.current) {
      enableBodyScroll(fullPageNavMenuRef.current);
    }
  };

  /** keyUp event handler */
  const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      if (navToggleWrapperRef.current) {
        navToggleWrapperRef.current.querySelector('button')?.focus();
      }
    }
  };

  useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;
      setIsOpen(false);

      // Skip to main content on screen readers
      if (isBeforeTablet) {
        const link = document.querySelector(
          `.skip-link[href="#${SkipLinkIds.Main}"]`,
        );
        if (link) {
          (link as HTMLAnchorElement).click();
        }
      }
    }

    clearAllBodyScrollLocks();
  }, [location]);

  const { isBeforeTablet, isAtLeastTablet } = useBreakpoint();

  return (
    <Wrapper ref={fullPageNavMenuRef} onKeyUp={onKeyUp}>
      {isBeforeTablet && links.length > 1 && (
        <nav
          role="navigation"
          aria-label={formatMessage(navMenuMessages.mobileNavMenu)}
        >
          {/* NavToggleWrapper is set to `display: none` on large screens */}
          <NavToggleWrapper ref={navToggleWrapperRef}>
            <NavToggleIcon
              buttonType="primary"
              onClick={isOpen ? closeNav : openNav}
              aria-label={formatMessage(
                isOpen
                  ? navMenuMessages.navToggleCloseButton
                  : navMenuMessages.navToggleOpenButton,
              )}
              aria-expanded={isOpen}
              aria-controls="menu"
            >
              <Icon type={isOpen ? 'close' : 'dots-vertical'} />
            </NavToggleIcon>
          </NavToggleWrapper>

          {isOpen && (
            /* There is no `display: none` responsive activity for this element */
            <FullPageWrapper>
              <LinksWrapper>
                <ul id="menu">
                  {links.map((link) => (
                    <li key={link.href + link.message.id}>
                      <NavMenuLink
                        selected={
                          getUnderlyingPath(location.pathname)
                            .underlyingPath === link.href
                        }
                        mode="fullpage"
                        {...link}
                      />
                    </li>
                  ))}
                </ul>
              </LinksWrapper>
            </FullPageWrapper>
          )}
        </nav>
      )}

      {/* NavBarWrapper is set to `display: none` on mobile screens */}
      {isAtLeastTablet && (
        <NavBarWrapper _hidden={links.length < 2}>
          {links.map((link) => (
            <NavMenuLink
              selected={
                getUnderlyingPath(location.pathname).underlyingPath ===
                link.href
              }
              key={link.href + link.message.id}
              mode="bar"
              {...link}
            />
          ))}
        </NavBarWrapper>
      )}
    </Wrapper>
  );
}
